import React, { useEffect, useState } from 'react';
import { wsClient } from '../services/websocketClient';
import { getApiBase } from '../services/apiBase';
import ReconnectionOverlay from './ReconnectionOverlay';
import { Language } from '../data/translations';
import { GameMode, MultiplayerState } from '../types';

interface GameConnectionManagerProps {
    auth: { token?: string } | null;
    gameMode: GameMode;
    multiplayerState: MultiplayerState;
    setGameMode: (mode: GameMode) => void;
    setMultiplayerState: (u: (prev: MultiplayerState) => MultiplayerState) => void;
    joinRoom: (roomCode: string, playerName: string, password?: string, token?: string) => void;
    leaveRoom: (intentional?: boolean) => void;
    handleNavigate: (mode: GameMode) => void;
    playerName: string;
    language: Language;
}

const GameConnectionManager: React.FC<GameConnectionManagerProps> = ({
    auth,
    gameMode,
    multiplayerState,
    setGameMode,
    setMultiplayerState,
    joinRoom,
    leaveRoom,
    handleNavigate,
    playerName,
    language
}) => {
    const [connectionStatus, setConnectionStatus] = useState<'CONNECTING' | 'CONNECTED' | 'DISCONNECTED' | 'ERROR' | 'RECONNECTING' | 'FAILED'>('DISCONNECTED');
    const [autoJoinRoom, setAutoJoinRoom] = useState<string | null>(null);
    const hasAutoJoined = React.useRef(false);

    useEffect(() => {
        return wsClient.onStatusChange(setConnectionStatus);
    }, []);

    useEffect(() => {
        console.log('[ConnectionManager] Auto-join check - auth:', !!auth?.token, 'gameMode:', gameMode, 'done:', hasAutoJoined.current);
        if (auth?.token && gameMode === 'MAIN_MENU' && !hasAutoJoined.current) {
            hasAutoJoined.current = true;
            const base = getApiBase();
            fetch(`${base}/api/active-session?token=${auth.token}`)
                .then(r => r.json())
                .then(data => {
                    console.log('[ConnectionManager] Active session response:', data);
                    if (data.ok && data.roomCode) {
                        if (data.wsAlive) {
                            console.log('[ConnectionManager] Active session found but WebSocket is alive in another tab. Skipping auto-join.');
                            return;
                        }
                        wsClient.connect();
                        setAutoJoinRoom(data.roomCode);
                    }
                })
                .catch(e => console.warn('[ConnectionManager] Auto-join check failed:', e));
        }
    }, [auth?.token, gameMode]);

    useEffect(() => {
        if (autoJoinRoom && auth?.token && connectionStatus === 'CONNECTED') {
            console.log('[ConnectionManager] WebSocket connected, executing auto-join to room:', autoJoinRoom);
            joinRoom(autoJoinRoom, playerName, '', auth.token);
            setAutoJoinRoom(null);
        } else if (autoJoinRoom && connectionStatus !== 'CONNECTED') {
            console.log('[ConnectionManager] Auto-join pending, waiting for WebSocket connection. Status:', connectionStatus);
        }
    }, [autoJoinRoom, joinRoom, playerName, auth?.token, connectionStatus]);

    const reconnectAttempt = wsClient.reconnectAttempts;
    const shouldShowReconnectionOverlay =
        gameMode === 'PLAYING' &&
        (
            connectionStatus === 'FAILED' ||
            connectionStatus === 'ERROR' ||
            (connectionStatus === 'RECONNECTING' && reconnectAttempt >= 4) ||
            (connectionStatus === 'CONNECTING' && reconnectAttempt >= 4)
        );

    const shouldShowOpponentReconnectOverlay =
        gameMode === 'PLAYING' &&
        connectionStatus === 'CONNECTED' &&
        !!multiplayerState.roomInfo &&
        (
            multiplayerState.roomInfo.players.length < 2 ||
            multiplayerState.opponentConnectionStatus === 'offline'
        );

    const handleExit = () => {
        leaveRoom(true);
        setMultiplayerState(prev => ({ ...prev, roomInfo: null }));
        setGameMode('MAIN_MENU');
        handleNavigate('MAIN_MENU');
    };

    if (shouldShowReconnectionOverlay) {
        return (
            <ReconnectionOverlay
                status={connectionStatus}
                language={language}
                onExit={handleExit}
            />
        );
    }

    if (shouldShowOpponentReconnectOverlay) {
        return (
            <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center pointer-events-auto">
                <div className="text-center font-cinzel text-white max-w-md p-8 border border-white/10 rounded-xl bg-black/50 shadow-2xl">
                    <div className="mb-6">
                        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                    <h2 className="text-xl text-amber-400 mb-2">
                        {language === 'ru' ? 'Ожидание переподключения соперника' : 'Waiting for Opponent'}
                    </h2>
                    <p className="text-gray-300 mb-6">
                        {language === 'ru' ? 'Матч сохранён. Комната остаётся активной, пока второй игрок возвращается.' : 'The match is preserved while the other player reconnects.'}
                    </p>
                    <button
                        onClick={handleExit}
                        className="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded shadow-lg transition-colors duration-200 border border-red-400/30"
                    >
                        {language === 'ru' ? 'Выйти в меню' : 'Exit to Menu'}
                    </button>
                </div>
            </div>
        );
    }

    return null;
};

export default GameConnectionManager;
